import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Loader from '../components/Loader';
import Logo from '../images/school_logo.png';
import '../styles/ClassDetailsPage.css';
import ClassInfo from '../components/ClassInfo';
import AddStudentsToClassModal from '../components/AddStudentsToClassModal';
import AddSubjectsToClassModal from '../components/AddSubjectsToClassModal';
import AddTeacherToSubjectModal from '../components/AddTeachersToSubjectModal';
import PromoteClassModal from '../components/PromoteClassModal';
import Modal from 'react-modal';
import { useUserAuth } from "../context/UserAuthContext";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import SuccessModal from './SuccessModal';
import {SCHOOL_ACRONYM } from '../Variables.js';

const ClassDetailsPage = () => {
  const { id } = useParams();
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [classInfo, setClassInfo] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [showFeesModal, setShowFeesModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [feesAmount, setFeesAmount] = useState(0);
  const [isEditStudentFeesModalOpen, setIsEditStudentFeesModalOpen] = useState(false);
  const [currentStudentFees, setCurrentStudentFees] = useState(null);
  const [paidAmount, setPaidAmount] = useState(0);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);  

  const navigate = useNavigate();

  const fetchData = async () => {
    setError("");
    setSuccessMessage("");
    setShowModal(false);
    setLoading(true);
    try {
      const classRef = doc(db, "classes", id);
      const classSnap = await getDoc(classRef);
      if (!classSnap.exists()) {
        setError("Class not found");
        setShowModal(true);
        setLoading(false);
        return;
      }
  
      const classData = classSnap.data();
  
      // Fetch names for section, session, term by IDs stored in classData
      const [sectionDoc, sessionDoc, termDoc] = await Promise.all([
        classData.section ? getDoc(doc(db, "sections", classData.section)) : null,
        classData.session ? getDoc(doc(db, "sessions", classData.session)) : null,
        classData.term ? getDoc(doc(db, "terms", classData.term)) : null,
      ]);
  
      const sectionName = sectionDoc?.exists() ? sectionDoc.data().name : "Unknown section";
      const sessionName = sessionDoc?.exists() ? sessionDoc.data().name : "Unknown session";
      const termName = termDoc?.exists() ? termDoc.data().name : "Unknown term";
  
      let formMasterData = null;
    
      // Only try to fetch formMaster if classData.formMaster exists and is a valid ID
      if (classData.formMaster && typeof classData.formMaster === 'string' && classData.formMaster.trim() !== '') {
        try {
          const formMasterDoc = await getDoc(doc(db, "users", classData.formMaster));
          if (formMasterDoc.exists() && formMasterDoc.data().status === "Teacher") {
            formMasterData = { id: formMasterDoc.id, ...formMasterDoc.data() };
          }
        } catch (error) {
          setError("Error fetching class teacher");
          setShowModal(true);
        }
      }
  
      // Fetch students by ID in classInfo.students
      const studentsRef = collection(db, "students");
      const allStudentsSnap = await getDocs(studentsRef);
      const allStudents = allStudentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const filteredStudents = allStudents.filter(student => classData.students?.includes(student.id));
      setStudents(filteredStudents);
  
      // Fetch subjects by ID in classInfo.subjects
      const subjectsRef = collection(db, "subjects");
      const allSubjectsSnap = await getDocs(subjectsRef);
      const allSubjects = allSubjectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const classSubjectIds = classData.subjects?.map(s => s.id) || [];
      const filteredSubjects = allSubjects.filter(subject => classSubjectIds.includes(subject.id));
      setSubjects(filteredSubjects);
  
      const teachersRef = collection(db, "users");
      const teachersSnap = await getDocs(teachersRef);
      const teachers = teachersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setTeachers(teachers);

      setClassInfo({
        ...classData,
        section: sectionName, // Store section name for display
        sectionId: classData.section || '', // Store section ID for promotion
        session: sessionName,
        term: termName,
        formMaster: formMasterData,
      });
    } catch (error) {
      setError("Class not found");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [id]);
  
  const calculateFinancialTotals = () => {
    let expectedIncome = 0;
    let actualIncome = 0;
    let outstandingBalance = 0;

    students.forEach(student => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      const balance = fees - paid;

      expectedIncome += fees;
      actualIncome += paid;
      outstandingBalance += balance;
    });

    return {
      expectedIncome,
      actualIncome,
      outstandingBalance
    };
  };

  const financialTotals = calculateFinancialTotals();

  const generateOutstandingFeesPDF = () => {
    // Filter students with balance > 0
    const studentsWithBalance = students.filter(student => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      return (fees - paid) > 0;
    });
  
    if (studentsWithBalance.length === 0) {
      setError("No students with outstanding balances");
      setShowModal(true);
      return;
    }
  
    // Calculate totals
    let totalFees = 0;
    let totalPaid = 0;
    let totalBalance = 0;
  
    // Prepare data for the table and calculate totals
    const tableData = studentsWithBalance.map((student, index) => {
      const enrollment = student.enrollments?.find(e => e.classId === id) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      const balance = fees - paid;
  
      // Add to totals
      totalFees += fees;
      totalPaid += paid;
      totalBalance += balance;
  
      return [
        index + 1,
        `${student.firstName} ${student.lastName}`,
        student.fathersName || 'N/A',
        student.fathersPhone1 || 'N/A',
        fees.toLocaleString(),
        paid.toLocaleString(),
        balance.toLocaleString()
      ];
    });
  
    // Add totals row
    const totalsRow = [
      '',
      'TOTAL',
      '',
      '',
      totalFees.toLocaleString(),
      totalPaid.toLocaleString(),
      totalBalance.toLocaleString()
    ];
  
    // Create new PDF document
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString();
  
    // School information header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'normal');
    doc.text(`${SCHOOL_ACRONYM} Outstanding Fees Report`, 105, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.text(`Class: ${classInfo.name} (${classInfo.session}-${classInfo.term} Term)`, 105, 25, { align: 'center' });
    doc.text(`Generated on: ${currentDate}`, 105, 32, { align: 'center' });
    doc.text(`Cashier: ${userData.firstName} ${userData.lastName}`, 105, 39, { align: 'center' });
  
    // Generate the table
    autoTable(doc, {
      head: [
        ['S/N', 'Student Name', "Father's Name", "Father's Phone", 'Fees', 'Paid', 'Balance']
      ],
      body: [...tableData, totalsRow],
      startY: 45,
      styles: {
        cellPadding: 2,
        fontSize: 10,
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 10, minCellHeight: 10, textColor: [0, 0, 0] },
        1: { cellWidth: 40, minCellHeight: 10, textColor: [0, 0, 0] },
        2: { cellWidth: 40, minCellHeight: 10, textColor: [0, 0, 0] },
        3: { cellWidth: 30, minCellHeight: 10, textColor: [0, 0, 0] },
        4: { cellWidth: 20, minCellHeight: 10, textColor: [0, 0, 0] },
        5: { cellWidth: 20, minCellHeight: 10, textColor: [0, 0, 0] },
        6: { cellWidth: 20, minCellHeight: 10, textColor: [255, 0, 0] }
      },

      didDrawPage: function (data) {
        const finalY = data.cursor.y + 10;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Total Expected Income: N ${totalFees.toLocaleString()}`, 14, finalY);
        doc.text(`Total Actual Income: N ${totalPaid.toLocaleString()}`, 14, finalY + 6);
        doc.text(`Total Outstanding: N ${(totalFees - totalPaid).toLocaleString()}`, 14, finalY + 12);
      }
    });
  
    // Save the PDF
    doc.save(`Outstanding_Fees_${classInfo.name}_${currentDate.replace(/[/:, ]/g, '_')}.pdf`);
  };

  const openTeacherModal = (subject) => {
    setSelectedSubject(subject);
    setShowTeacherModal(true);
  };
  
  if (loading) return <Loader text="LOADING" />;
  if (!classInfo) return <p className='bold caps align-center justify-center'>Class not found</p>;

  return (
    <div className="login-page-items column align-center">
        <div className="top-elements row align-center space-between">
          <button onClick={() => navigate(-1)} className="back-link back-link1">
              <i className="fa-solid fa-arrow-left size3"></i>
          </button>
          <div className="column align-center justify-center">
            <img src={Logo} className="middle-logo" alt="logo" />
          </div>
          <Link to="#" className="home-link" onClick={() => window.location.reload()}>
            <i className="fa-solid fa-rotate-right size4"></i>
          </Link>
        </div>

        <h2 className="manage-page-heading size4">{classInfo.name} ({classInfo.session}-{classInfo.term} Term)</h2>
        <div className="tab-nav row align-center gap-2">
          <button className={`tab-button ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>Info</button>
          <button className={`tab-button ${activeTab === 'subjects' ? 'active' : ''}`} onClick={() => setActiveTab('subjects')}>Subjects</button>
          <button className={`tab-button ${activeTab === 'students' ? 'active' : ''}`} onClick={() => setActiveTab('students')}>Students</button>
          {(userData?.status === 'Cashier' || userData?.level === 'Supreme') && (
            <button className={`tab-button ${activeTab === 'fees' ? 'active' : ''}`} onClick={() => setActiveTab('fees')}>Fees</button>
          )}          
        </div>
        
        <AddStudentsToClassModal
          isOpen={showStudentModal}
          classId={id}
          onClose={() => setShowStudentModal(false)}
          onSuccess={fetchData}
        />
        <AddSubjectsToClassModal
          isOpen={showSubjectModal}
          classId={id}
          onClose={() => setShowSubjectModal(false)}
          onSuccess={fetchData}
        />
        <AddTeacherToSubjectModal
          isOpen={showTeacherModal}
          onClose={() => setShowTeacherModal(false)}
          classId={id}
          subject={selectedSubject}
          teachers={teachers} 
          onSuccess={fetchData}
        />
        <PromoteClassModal
          isOpen={showPromoteModal}
          onClose={() => setShowPromoteModal(false)}
          classId={id}
          currentClass={classInfo}
          students={students}
          subjects={subjects}
          onSuccess={() => {
            fetchData();
            navigate('/classes');
          }}
        />

      <div className="tab-content scroll-fixed scroll-fixed2">
        {activeTab === 'info' && <ClassInfo
            classInfo={classInfo}
            studentCount={students.length}
            subjectCount={subjects.length}
            expectedIncome={financialTotals.expectedIncome}
            actualIncome={financialTotals.actualIncome}
            outstandingBalance={financialTotals.outstandingBalance}
            setShowPromoteModal={setShowPromoteModal}
        />}
        {activeTab === 'students' && (
          <div className="tab-section">
            <div className="tab-section-top column align-center">
              <button className='tab-section-button caps' onClick={() => setShowStudentModal(true)}>Manage Students</button>
            </div>
            {students.length === 0 ? <p className='text-center male-female-stat'>No students enrolled</p> : (
              <div>
                {students.map(stu => (
                  <div key={stu.id} className="info-item info-item1">
                    <span className="font-gold bold size5">{stu.admissionNumber}</span><br />
                    <span>{stu.firstName} {stu.lastName} {stu.middleName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'subjects' && (
          <div className="tab-section">
            <div className="tab-section-top column align-center">
              <button className='tab-section-button caps' onClick={() => setShowSubjectModal(true)}>Manage Subjects</button>
            </div>
            {subjects.length === 0 ? <p className='text-center male-female-stat'>No subjects assigned</p> : (
              <div>
                {subjects.map(sub => {
                  // Find all teachers assigned to this subject in this class
                  const assignedTeachers = teachers.filter(teacher => 
                    teacher.teachingAssignments?.some(assignment => 
                      assignment.classId === id && assignment.subjectId === sub.id
                    )
                  );

                  return (
                    <div key={sub.id} className="info-item info-item1 row space-between">
                      <div>
                        <span className="font-gold bold size5">
                          {assignedTeachers.length > 0 
                            ? assignedTeachers.map(t => `${t.firstName} ${t.lastName}`).join(', ') 
                            : 'No teacher'}
                        </span><br />
                        <span>{sub.name} ({sub.code})</span>
                      </div>                         
                      <button className="list-btn" onClick={() => openTeacherModal(sub)}>
                        {/* {assignedTeachers.length > 0 ? 'Manage Teachers' : 'Assign Teacher(s)'} */}
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )} 
        {activeTab === 'fees' && (
          <div className="tab-section">
            <div className="tab-section-top row justify-center align-center gap-15">
              <button 
                className='tab-section-button caps' 
                onClick={() => {
                  setFeesAmount(0);
                  setShowFeesModal(true);
                }}
              >
                Assign Fees
              </button>
              <button 
                className='tab-section-button caps' 
                onClick={generateOutstandingFeesPDF}
              >
                Print Report
              </button>
            </div>
            
            {students.length === 0 ? (
              <p className='text-center male-female-stat'>No students enrolled</p>
            ) : (
              <div className="fees-table-container">                
                <table className="fees-table">
                  <thead>
                    <tr>
                      <th style={{ width: "200px" }}>Student</th>
                      <th style={{ width: "150px" }}>Fees</th>
                      <th style={{ width: "150px" }}>Paid</th>
                      <th style={{ width: "150px" }}>Bal</th>
                      <th style={{ width: "120px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => {
                      // Safely get the enrollment for this class
                      const enrollment = student.enrollments?.find(e => e.classId === id) || {
                        fees: 0,
                        paid: 0
                      };
                      
                      // Ensure we have numbers for calculations
                      const fees = Number(enrollment.fees) || 0;
                      const paid = Number(enrollment.paid) || 0;
                      const balance = fees - paid;

                      return (
                        <tr key={student.id}>
                          <td style={{ width: "200px" }}>{student.firstName} {student.lastName}</td>
                          <td style={{ width: "150px" }}>{fees.toLocaleString()}</td>
                          <td style={{ width: "150px" }}>{paid.toLocaleString()}</td>
                          <td style={{ width: "150px" }}>{balance.toLocaleString()}</td>
                          <td style={{ width: "120px" }}>
                            <button 
                              className="list-btn"
                              onClick={() => {
                                setCurrentStudentFees(student);
                                setFeesAmount(fees);
                                setPaidAmount(paid);
                                setIsEditStudentFeesModalOpen(true);
                              }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}       
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}
      {/* Assign Fees to Class Modal */}
      <Modal
        isOpen={showFeesModal}
        onRequestClose={() => setShowFeesModal(false)}
        contentLabel="Assign Fees to Class"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <h2>Assign Fees to {classInfo.name}</h2>
          <div className="modal-form">
            <label className='text-left'>Enter Fees</label>
            <input
              type="number"
              className="input-field"
              placeholder="Fees Amount"
              value={feesAmount === 0 ? '' : feesAmount}
              onChange={(e) => setFeesAmount(e.target.value)}
            />
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setShowFeesModal(false)}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    // Update fees for all students in the class
                    await Promise.all(
                      students.map(async (student) => {
                        const studentRef = doc(db, "students", student.id);
                        const studentDoc = await getDoc(studentRef);
                        const studentData = studentDoc.data();
                        
                        let enrollments = studentData.enrollments || [];
                        const existingIndex = enrollments.findIndex(e => e.classId === id);
                        
                        if (existingIndex >= 0) {
                          enrollments[existingIndex] = {
                            ...enrollments[existingIndex],
                            fees: Number(feesAmount)
                          };
                        } else {
                          enrollments.push({
                            classId: id,
                            fees: Number(feesAmount || 0),
                            paid: 0
                          });
                        }
                        
                        await updateDoc(studentRef, { enrollments });
                      })
                    );
                    
                    setShowFeesModal(false);
                    fetchData(); // Refresh the data
                  } catch (error) {
                    setError("Error assigning fees");
                    setShowModal(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Assign Fees
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Student Fees Modal */}
      <Modal
        isOpen={isEditStudentFeesModalOpen}
        onRequestClose={() => setIsEditStudentFeesModalOpen(false)}
        contentLabel="Edit Student Fees"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <h2>Update Fees for {currentStudentFees?.firstName} {currentStudentFees?.lastName}</h2>
          <label className='text-left'>Fees Amount</label>
          <div className="modal-form">
            <input
              type="number"
              className="input-field"
              placeholder="Fees Amount"
              value={feesAmount}
              onChange={(e) => setFeesAmount(e.target.value)}
            />
            <label className='text-left'>Paid Amount</label>
            <input
              type="number"
              className="input-field"
              placeholder="Paid Amount"
              value={paidAmount === 0 ? '' : paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
            />
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setIsEditStudentFeesModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    const studentRef = doc(db, "students", currentStudentFees.id);
                    const studentDoc = await getDoc(studentRef);
                    const studentData = studentDoc.data();
                    
                    let enrollments = studentData.enrollments || [];
                    const existingIndex = enrollments.findIndex(e => e.classId === id);
                    
                    if (existingIndex >= 0) {
                      enrollments[existingIndex] = {
                        ...enrollments[existingIndex],
                        fees: Number(feesAmount),
                        paid: Number(paidAmount || 0)
                      };
                    } else {
                      enrollments.push({
                        classId: id,
                        fees: Number(feesAmount),
                        paid: Number(paidAmount || 0)
                      });
                    }
                    
                    await updateDoc(studentRef, { enrollments });
                    
                    setIsEditStudentFeesModalOpen(false);
                    fetchData(); // Refresh the data
                  } catch (error) {
                    setError("Error updating student fees");
                    setShowModal(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClassDetailsPage;